import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { GeoJsonFeature } from '../../models/geo-json-feature';
import { FeatureInspectComponent } from '../../shared/feature-inspect/feature-inspect.component';

type FeatureFull = GeoJsonFeature['properties'];
import { NuevaFichaGeoJSON, NuevaFeature } from '../../models/nueva-ficha';
import { showAlert } from '../../tools/alerts';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-create-wonder',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, FeatureInspectComponent],
  templateUrl: './create-wonder.component.html',
  styleUrl: './create-wonder.component.css',
})
export class CreateWonderComponent implements OnInit {

  private fb = inject(FormBuilder);
  private dataService = inject(DataService);

  // Vista: 'list' o 'form'
  viewMode: 'list' | 'form' = 'list';

  // Popup inspeccionar
  showInspectModal = false;
  itemToInspect: FeatureFull | null = null;

  openInspectModal(item: FeatureFull): void {
    this.itemToInspect = item;
    this.showInspectModal = true;
  }

  closeInspectModal(): void {
    this.showInspectModal = false;
    this.itemToInspect = null;
  }


  // Popup eliminar
  showDeleteModal = false;
  itemToDelete: FeatureFull | null = null;

  openDeleteModal(item: FeatureFull): void {
    this.itemToDelete = item;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.itemToDelete = null;
  }

  isDeleting = false;

  confirmDelete(): void {
    if (!this.itemToDelete) return;
    this.isDeleting = true;
    this.dataService.deleteWonder(this.itemToDelete.globalid).subscribe({
      next: () => {
        showAlert('Éxito', 'Maravilla eliminada correctamente', 'success');
        this.allFeatures = this.allFeatures.filter(f => f.globalid !== this.itemToDelete!.globalid);
        this.applyFilters();
        this.closeDeleteModal();
        this.isDeleting = false;
      },
      error: () => {
        showAlert('Error', 'Error al eliminar la maravilla', 'error');
        this.closeDeleteModal();
        this.isDeleting = false;
      }
    });
  }

  // Listado
  allFeatures: FeatureFull[] = [];
  filteredFeatures: FeatureFull[] = [];
  filterCategory = '';
  filterText = '';
  isLoading = true;

  private allowedCategories = ['playa', 'arq', 'natural', 'evento'];

  // Formulario
  currentStep = 0;
  totalSteps = 4;
  isSubmitting = false;

  sectionTitles = [
    'Datos generales',
    'Ubicación',
    'Descripciones',
    'Campos específicos'
  ];

  categorias = [
    { value: 'playa', label: 'Playa' },
    { value: 'arq', label: 'Arquitectura' },
    { value: 'natural', label: 'Naturaleza' },
    { value: 'evento', label: 'Evento' },
  ];

  tiposPlaya = [
    { value: 'arena', label: 'Arena', eng: 'sand' },
    { value: 'piedra', label: 'Piedra', eng: 'rocky' },
  ];

  peligrosidades = [
    { value: 'baja', label: 'Baja' },
    { value: 'media', label: 'Media' },
    { value: 'alta', label: 'Alta' },
  ];

  meses = [
    { value: 'Enero', eng: 'January' },
    { value: 'Febrero', eng: 'February' },
    { value: 'Marzo', eng: 'March' },
    { value: 'Abril', eng: 'April' },
    { value: 'Mayo', eng: 'May' },
    { value: 'Junio', eng: 'June' },
    { value: 'Julio', eng: 'July' },
    { value: 'Agosto', eng: 'August' },
    { value: 'Septiembre', eng: 'September' },
    { value: 'Octubre', eng: 'October' },
    { value: 'Noviembre', eng: 'November' },
    { value: 'Diciembre', eng: 'December' },
  ];

  ngOnInit() {
    this.loadFeatures();
  }

  loadFeatures() {
    this.isLoading = true;
    this.dataService.getGeoJson().subscribe({
      next: (data) => {
        this.allFeatures = data.features
          .map(f => f.properties)
          .filter(f => this.allowedCategories.includes(f.categoria));
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  applyFilters() {
    let result = this.allFeatures;
    if (this.filterCategory) {
      result = result.filter(f => f.categoria === this.filterCategory);
    }
    if (this.filterText.trim()) {
      const term = this.filterText.trim().toLowerCase();
      result = result.filter(f => f.denominacion.toLowerCase().includes(term));
    }
    this.filteredFeatures = result;
  }

  setFilterCategory(cat: string) {
    this.filterCategory = this.filterCategory === cat ? '' : cat;
    this.applyFilters();
  }

  showForm() {
    this.viewMode = 'form';
    this.currentStep = 0;
    window.scrollTo(0, 0);
  }

  backToList() {
    this.viewMode = 'list';
    this.resetForm();
    this.loadFeatures();
    window.scrollTo(0, 0);
  }

  coordError = '';

  form: FormGroup = this.fb.group({
    // Paso 1: Generales
    categoria: ['', Validators.required],
    denominacion_esp: ['', Validators.required],
    denominacion_ing: ['', Validators.required],

    // Paso 2: Ubicación
    enlace_maps: ['', Validators.required],
    latitud: ['', Validators.required],
    longitud: ['', Validators.required],

    // Paso 3: Descripciones
    descripcion_esp: ['', Validators.required],
    descripcion_ing: ['', Validators.required],
    resumen_esp: [''],
    resumen_ing: [''],
    obs: [''],

    // Paso 4: Playa
    playa_piedra_esp: [''],
    peligrosidad_esp: [''],
    duchas: ['false'],
    restauracion: ['false'],
    parking: ['false'],
    accesible: ['false'],
    descripcion_parking_esp: [''],
    descripcion_parking_ing: [''],

    // Paso 4: Natural
    nat_origen_geologico_esp: [''],
    nat_origen_geologico_ing: [''],
    nat_precio_entrada: [0],
    nat_banos: ['false'],
    nat_parking: ['false'],
    nat_parking_descripcion_esp: [''],
    nat_parking_descripcion_ing: [''],
    nat_oficina_informacion: ['false'],
    nat_restauracion: ['false'],

    // Paso 4: Evento
    evt_historia_esp: [''],
    evt_historia_ing: [''],
    evt_parking: ['false'],
    evt_descripcion_parking_esp: [''],
    evt_descripcion_parking_ing: [''],
    evt_precio_entrada: [0],
    evt_web: [''],
    evt_mes_esp: [''],
    evt_horario_esp: [''],
    evt_horario_ing: [''],

    // Paso 4: Arquitectura
    arq_precio_entrada: [0],
    arq_horario_esp: [''],
    arq_horario_ing: [''],
    arq_historia_esp: [''],
    arq_historia_ing: [''],
    arq_parking: ['false'],
    arq_parking_descripcion_esp: [''],
    arq_parking_descripcion_ing: [''],
  });

  // Campos requeridos por paso
  private stepFields: string[][] = [
    ['categoria', 'denominacion_esp', 'denominacion_ing'],
    ['enlace_maps', 'latitud', 'longitud'],
    ['descripcion_esp', 'descripcion_ing'],
    [],
  ];

  generateCoords() {
    const url = this.form.get('enlace_maps')?.value || '';
    this.coordError = '';

    // Match /@lat,lng,zoom pattern
    const match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+),\d+z/);
    if (match) {
      this.form.get('latitud')?.setValue(match[1]);
      this.form.get('longitud')?.setValue(match[2]);
    } else {
      this.coordError = 'No se pudieron extraer las coordenadas. Asegúrate de que la URL tiene el formato correcto (contiene /@lat,lng,zoom).';
      this.form.get('latitud')?.setValue('');
      this.form.get('longitud')?.setValue('');
    }
  }

  clearUrl() {
    this.form.get('enlace_maps')?.setValue('');
    this.form.get('latitud')?.setValue('');
    this.form.get('longitud')?.setValue('');
    this.coordError = '';
  }

  toggleField(field: string) {
    const current = this.form.get(field)?.value;
    this.form.get(field)?.setValue(current === 'true' ? 'false' : 'true');
  }

  getCategoriaLabel(value: string): string {
    return this.categorias.find(c => c.value === value)?.label ?? value;
  }

  nextStep() {
    if (this.validateStep(this.currentStep)) {
      if (this.currentStep < this.totalSteps - 1) {
        this.currentStep++;
        window.scrollTo(0, 0);
      }
    } else {
      this.markStepTouched(this.currentStep);
    }
  }

  prevStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      window.scrollTo(0, 0);
    }
  }

  private validateStep(step: number): boolean {
    const fields = this.stepFields[step] || [];
    return fields.every(f => this.form.get(f)?.valid);
  }

  private markStepTouched(step: number) {
    const fields = this.stepFields[step] || [];
    fields.forEach(f => this.form.get(f)?.markAsTouched());
  }

  onSubmit() {
    // Validar todos los pasos
    for (let i = 0; i < this.totalSteps; i++) {
      if (!this.validateStep(i)) {
        this.currentStep = i;
        this.markStepTouched(i);
        return;
      }
    }

    this.isSubmitting = true;
    const payload = this.buildPayload();
    console.log('Nueva ficha:', payload);

    this.dataService.createWonder(payload)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: () => {
          showAlert('Creada', 'La maravilla se ha creado correctamente.', 'success');
          this.backToList();
        },
        error: (err) => {
          console.error('Error al crear maravilla:', err);
          const msg = err.error?.detail || 'Ha ocurrido un error al crear la maravilla.';
          showAlert('Error', msg, 'error');
        }
      });
  }

  private buildPayload(): NuevaFichaGeoJSON {
    const v = this.form.getRawValue();
    const lat = Number(v.latitud);
    const lng = Number(v.longitud);

    const tipoPlayaMap: Record<string, string> = {
      arena: 'sand',
      piedra: 'rocky',
    };

    const peligrosidadMap: Record<string, string> = {
      baja: 'low',
      media: 'medium',
      alta: 'high',
    };

    const baseEsp: any = {
      OBJECTID: 0,
      denominacion: v.denominacion_esp,
      obs: v.obs || '',
      globalid: 'TEMP',
      categoria: v.categoria,
      fotos_web: '',
      descripcion: v.descripcion_esp,
      enlace_maps: v.enlace_maps,
      longitud: lng,
      latitud: lat,
      descripcion_eng: v.descripcion_ing,
      resumen_informativo: v.resumen_esp || '',
      keywords: '',
    };

    const baseIng: any = {
      OBJECTID: 0,
      denominacion: v.denominacion_ing,
      obs: v.obs || '',
      globalid: 'TEMP',
      categoria: v.categoria,
      fotos_web: '',
      descripcion: v.descripcion_ing,
      enlace_maps: v.enlace_maps,
      longitud: lng,
      latitud: lat,
      descripcion_eng: v.descripcion_ing,
      resumen_informativo: v.resumen_ing || '',
      keywords: '',
    };

    // Añadir campos específicos según categoría
    if (v.categoria === 'playa') {
      Object.assign(baseEsp, {
        duchas: v.duchas,
        restauracion_en_la_playa: v.restauracion,
        parking: v.parking,
        descripcion_parking: v.descripcion_parking_esp || '',
        accesible_minusvalidos: v.accesible,
        playa_piedra: v.playa_piedra_esp || '',
        peligrosidad_playa: v.peligrosidad_esp || '',
      });

      Object.assign(baseIng, {
        duchas: v.duchas,
        restauracion_en_la_playa: v.restauracion,
        parking: v.parking,
        descripcion_parking: v.descripcion_parking_ing || '',
        accesible_minusvalidos: v.accesible,
        playa_piedra: tipoPlayaMap[v.playa_piedra_esp] || v.playa_piedra_esp || '',
        peligrosidad_playa: peligrosidadMap[v.peligrosidad_esp] || v.peligrosidad_esp || '',
      });
    }

    if (v.categoria === 'natural') {
      Object.assign(baseEsp, {
        origen_geologico: v.nat_origen_geologico_esp || '',
        precio_entrada: Number(v.nat_precio_entrada) || 0,
        baños: v.nat_banos,
        parking: v.nat_parking,
        parking_descripcion: v.nat_parking_descripcion_esp || '',
        oficina_de_informacion: v.nat_oficina_informacion,
        restauracion_dentro_del_paraje: v.nat_restauracion,
      });

      Object.assign(baseIng, {
        origen_geologico: v.nat_origen_geologico_ing || '',
        precio_entrada: Number(v.nat_precio_entrada) || 0,
        baños: v.nat_banos,
        parking: v.nat_parking,
        parking_descripcion: v.nat_parking_descripcion_ing || '',
        oficina_de_informacion: v.nat_oficina_informacion,
        restauracion_dentro_del_paraje: v.nat_restauracion,
      });
    }

    if (v.categoria === 'evento') {
      const mesEng = this.meses.find(m => m.value === v.evt_mes_esp)?.eng || v.evt_mes_esp || '';
      Object.assign(baseEsp, {
        historia: v.evt_historia_esp || '',
        parking: v.evt_parking,
        descripcion_parking: v.evt_descripcion_parking_esp || '',
        precio_entrada: Number(v.evt_precio_entrada) || 0,
        web: v.evt_web || '',
        mes: v.evt_mes_esp || '',
        horario: v.evt_horario_esp || '',
      });

      Object.assign(baseIng, {
        historia: v.evt_historia_ing || '',
        parking: v.evt_parking,
        descripcion_parking: v.evt_descripcion_parking_ing || '',
        precio_entrada: Number(v.evt_precio_entrada) || 0,
        web: v.evt_web || '',
        mes: mesEng,
        horario: v.evt_horario_ing || '',
      });
    }

    if (v.categoria === 'arq') {
      Object.assign(baseEsp, {
        precio_entrada: Number(v.arq_precio_entrada) || 0,
        horario: v.arq_horario_esp || '',
        historia: v.arq_historia_esp || '',
        parking: v.arq_parking,
        parking_descripcion: v.arq_parking_descripcion_esp || '',
      });

      Object.assign(baseIng, {
        precio_entrada: Number(v.arq_precio_entrada) || 0,
        horario: v.arq_horario_ing || '',
        historia: v.arq_historia_ing || '',
        parking: v.arq_parking,
        parking_descripcion: v.arq_parking_descripcion_ing || '',
      });
    }

    const buildFeature = (props: any): NuevaFeature => ({
      type: 'Feature',
      id: 0,
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      properties: props,
    });

    return {
      esp: buildFeature(baseEsp),
      ing: buildFeature(baseIng),
    };
  }

  private resetForm() {
    this.form.reset({
      categoria: '',
      denominacion_esp: '',
      denominacion_ing: '',
      enlace_maps: '',
      latitud: '',
      longitud: '',
      descripcion_esp: '',
      descripcion_ing: '',
      resumen_esp: '',
      resumen_ing: '',
      obs: '',
      playa_piedra_esp: '',
      peligrosidad_esp: '',
      duchas: 'false',
      restauracion: 'false',
      parking: 'false',
      accesible: 'false',
      descripcion_parking_esp: '',
      descripcion_parking_ing: '',
      nat_origen_geologico_esp: '',
      nat_origen_geologico_ing: '',
      nat_precio_entrada: 0,
      nat_banos: 'false',
      nat_parking: 'false',
      nat_parking_descripcion_esp: '',
      nat_parking_descripcion_ing: '',
      nat_oficina_informacion: 'false',
      nat_restauracion: 'false',
      evt_historia_esp: '',
      evt_historia_ing: '',
      evt_parking: 'false',
      evt_descripcion_parking_esp: '',
      evt_descripcion_parking_ing: '',
      evt_precio_entrada: 0,
      evt_web: '',
      evt_mes_esp: '',
      evt_horario_esp: '',
      evt_horario_ing: '',
      arq_precio_entrada: 0,
      arq_horario_esp: '',
      arq_horario_ing: '',
      arq_historia_esp: '',
      arq_historia_ing: '',
      arq_parking: 'false',
      arq_parking_descripcion_esp: '',
      arq_parking_descripcion_ing: '',
    });
    this.coordError = '';
    this.currentStep = 0;
    window.scrollTo(0, 0);
  }
}
